import React, { useEffect, useMemo, useState } from 'react';
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';

import DeviceApi from '../../../axios/Device/DeviceApi';
import { useLoading } from '../../../context/LoadingContext';
import { ControlpanelSuccess3thButton } from '../../../components/StyledButton';

ChartJS.register(ArcElement, Tooltip, Legend);

const DevicesChart = (props) => {
   const {
      refreshData,
      setDialogTitle,
      setDialogListDevice,
      setOpenShowInfoDeviceDialog,
   } = props;

   const [show, hide] = useLoading();
   const { t } = useTranslation();
   const navigate = useNavigate();

   const { getDeviceList } = DeviceApi();

   const [userDevices, setUserDevice] = useState(0);
   const [groupDevices, setGroupDevice] = useState(0);

   const [userDevicesList, setUserDeviceList] = useState([]);
   const [groupDevicesList, setGroupDeviceList] = useState([]);
   const [companyDevicesList, setCompanyDeviceList] = useState([]);

   const totalDevices =
      userDevices +
      groupDevices +
      companyDevicesList.length;

   const [chartData, setChartData] = useState({
      labels: [
         t('common.personal'),
         t('common.group'),
         t('common.company'),
      ],
      datasets: [
         {
            data: [0, 0, 0],
            backgroundColor: [
               '#D00D00',
               '#853EEF',
               '#FFD41D',
            ],
            borderWidth: 0,
            spacing: 8,
            borderRadius: 12,
         },
      ],
   });

   const centerTextPlugin = {
      id: 'deviceCenterText',
      afterDraw(chart) {
         const { ctx } = chart;
         const meta = chart.getDatasetMeta(0);

         if (!meta?.data?.length) return;

         const x = meta.data[0].x;
         const y = meta.data[0].y;

         ctx.save();

         ctx.textAlign = 'center';

         const label =
            t('common.total-device') ||
            'Total device';

         ctx.fillStyle = '#1F2937';
         ctx.font = '500 14px Montserrat';

         const maxWidth = 100;

         if (
            ctx.measureText(label).width >
            maxWidth
         ) {
            const words = label.split(' ');
            const middle = Math.ceil(
               words.length / 2
            );

            ctx.fillText(
               words
                  .slice(0, middle)
                  .join(' '),
               x,
               y - 30
            );

            ctx.fillText(
               words
                  .slice(middle)
                  .join(' '),
               x,
               y - 12
            );
         } else {
            ctx.fillText(
               label,
               x,
               y - 20
            );
         }

         ctx.fillStyle = '#111827';
         ctx.font = '700 32px Montserrat';

         ctx.fillText(
            totalDevices.toString(),
            x,
            y + 20
         );

         ctx.restore();
      },
   };

   const fetchData = async () => {
      try {
         show();

         const { data } = await getDeviceList(
            {
               pageNo: 1,
               recordPerPage: 500,
            },
            localStorage.getItem(
               'i18nextLngId'
            )
         );

         if (
            data.message === 'Successful' ||
            data.message === 'Thành công'
         ) {
            const listUserDevices = [];
            const listGroupDevices = [];
            const listCompanyDevices = [];

            data.data.forEach((item) => {
               if (item.userId !== null) {
                  listUserDevices.push(item);
               }

               if (item.groupId !== null) {
                  listGroupDevices.push(item);
               }

               if (
                  item.groupId == null &&
                  item.userId == null
               ) {
                  listCompanyDevices.push(item);
               }
            });

            const userCount =
               listUserDevices.length;
            const groupCount =
               listGroupDevices.length;
            const companyCount =
               listCompanyDevices.length;

            setUserDeviceList(
               listUserDevices
            );
            setGroupDeviceList(
               listGroupDevices
            );
            setCompanyDeviceList(
               listCompanyDevices
            );

            setUserDevice(userCount);
            setGroupDevice(groupCount);

            const activeSegments = [
               userCount,
               groupCount,
               companyCount,
            ].filter(item => item > 0).length;

            const hasMultipleSegments = activeSegments > 1;

            setChartData({
               labels: [
                  t('common.personal'),
                  t('common.group'),
                  t('common.company'),
               ],
               datasets: [
                  {
                     data: [
                        userCount,
                        groupCount,
                        companyCount,
                     ],
                     backgroundColor: [
                        '#D00D00',
                        '#853EEF',
                        '#FFD41D',
                     ],
                     borderWidth: 0,
                     spacing: hasMultipleSegments ? 2 : 0,
                     borderRadius: hasMultipleSegments ? 8 : 0,
                  },
               ],
            });
         }
      } catch (error) {
         if (
            error.response &&
            error.response.status !== 401
         ) {
            toast.error(
               t('common.content-030')
            );
         }
      } finally {
         hide();
      }
   };

   useEffect(() => {
      fetchData();
   }, [refreshData]);

   const options = useMemo(
      () => ({
         responsive: true,
         maintainAspectRatio: false,

         cutout: '68%',

         onClick(event, elements) {
            if (!elements.length) return;

            const index =
               elements[0].index;

            let title = '';
            let deviceList = [];

            if (index === 0) {
               title = t(
                  'common.content-176'
               );
               deviceList =
                  userDevicesList;
            } else if (index === 1) {
               title = t(
                  'common.content-177'
               );
               deviceList =
                  groupDevicesList;
            } else {
               title = t(
                  'common.content-198'
               );
               deviceList =
                  companyDevicesList;
            }

            setDialogTitle(title);
            setDialogListDevice(deviceList);
            setOpenShowInfoDeviceDialog(
               true
            );
         },

         plugins: {
            deviceCenterText: {
               total: totalDevices,
               title: t('common.total-devices'),
            },
            tooltip: {
               enabled: true,
            },

            legend: {
               display: true,
               position: 'bottom',
               align: 'center',                    // Căn giữa
               labels: {
                  usePointStyle: true,
                  pointStyle: 'circle',
                  boxHeight: 10,
                  boxWidth: 12,                     // Giảm kích thước box
                  padding: 12,                     // Giảm khoảng cách
                  useBorderRadius: true,
                  borderRadius: 999,
                  color: '#262626',
                  font: {
                     size: 11.5,                   // Giảm font một chút
                     family: "'Montserrat', sans-serif",
                     weight: '500',
                  },
                  // Thêm dòng này để hạn chế wrap
                  maxWidth: 300,                    // Giới hạn chiều rộng mỗi label
               },
            },
         },
      }),
      [
         userDevicesList,
         groupDevicesList,
         companyDevicesList,
         setDialogTitle,
         setDialogListDevice,
         setOpenShowInfoDeviceDialog,
         t,
      ]
   );

   return (
      <div className='card px-3 py-3 flex flex-col items-center h-full'>
         <div className='flex justify-center'>
            <div className='w-[300px] h-[240px]'>
               {totalDevices > 0 ? (
                  <Doughnut
                     data={chartData}
                     options={options}
                     plugins={[centerTextPlugin]}
                  />
               ) : (
                  <div className='h-full flex items-center justify-center'>
                     <p className='text-center text-gray-400'>
                        {t(
                           'common.content-034'
                        )}
                     </p>
                  </div>
               )}
            </div>
         </div>

         <ControlpanelSuccess3thButton
            onClick={() =>
               navigate(
                  '/controlpanel/devices-management'
               )
            }
            className='
               mt-4
               w-full
               rounded-2xl
               border
               border-[#C7D2FE]
               bg-[#EEF2FF]
               text-[#1557F5]
               shadow-none
            '
         >
            <AddIcon />

            <p>
               {t(
                  'button.register-devices'
               )}
            </p>
         </ControlpanelSuccess3thButton>
      </div>
   );
};

export default DevicesChart;